import { themeScoreLabel } from "../lib/scoring";
import { scoreToTier } from "../lib/results-garden";
import type { BloomThemeScore } from "../types/bloom-content";

interface ResultsOverviewProps {
  scores: BloomThemeScore[];
  onPrint: () => void;
}

const TIER_LABELS: Record<number, string> = {
  1: "Tijd om hier aandacht aan te geven",
  2: "Aan het groeien, maar extra verzorging nodig",
  3: "In volle bloei",
};

export function ResultsOverview({ scores, onPrint }: ResultsOverviewProps) {
  const totalScore = scores.reduce((sum, s) => sum + s.score, 0);
  const totalMax = scores.reduce((sum, s) => sum + s.maxScore, 0);
  const overallTier = scoreToTier(totalScore, totalMax);

  return (
    <section className="bloom-overview" aria-label="Resultatenoverzicht">
      <div className="bloom-overview__head">
        <div>
          <h3 className="bloom-overview__title">Jouw resultaten</h3>
          <p className="bloom-overview__subtitle">
            Een overzicht van je scores op de zes thema&apos;s.
          </p>
        </div>
        <button
          type="button"
          className="bloom-btn bloom-btn--ghost bloom-btn--small bloom-overview__print"
          onClick={onPrint}
        >
          Printen / PDF
        </button>
      </div>

      <div className="bloom-overview__stats">
        <div className="bloom-overview__stat">
          <span className="bloom-overview__stat-label">Totaalscore psychologische flexibiliteit</span>
          <p className="bloom-overview__stat-value">
            {totalScore}
            <span className="bloom-overview__stat-max"> / {totalMax}</span>
          </p>
          <span className="bloom-overview__stat-hint">punten</span>
        </div>
        <div className={`bloom-overview__stat bloom-overview__stat--n${overallTier}`}>
          <span className="bloom-overview__stat-label">Niveau</span>
          <p className="bloom-overview__stat-value">
            {overallTier}
            <span className="bloom-overview__stat-max"> / 3</span>
          </p>
          <span className="bloom-overview__stat-hint">{TIER_LABELS[overallTier]}</span>
        </div>
      </div>

      <BloomChart scores={scores} />

      <ol className="bloom-overview__themes">
        {scores.map((s) => {
          const tier = scoreToTier(s.score, s.maxScore);
          return (
            <li
              key={s.themeId}
              className={`bloom-overview__theme bloom-overview__theme--n${tier}`}
            >
              <div className="bloom-overview__theme-head">
                <h4 className="bloom-overview__theme-name">{s.label}</h4>
                <span className="bloom-overview__theme-tier">
                  Niveau {tier}
                </span>
              </div>
              <p className="bloom-overview__theme-score">
                <span className="bloom-overview__theme-score-value">{s.score}</span>
                <span className="bloom-overview__theme-score-max">/ {s.maxScore}</span>
                <span className="bloom-overview__theme-score-label">
                  {themeScoreLabel(s.score, s.maxScore)}
                </span>
              </p>
              <p className="bloom-overview__theme-summary">{s.summary}</p>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function BloomChart({ scores }: { scores: BloomThemeScore[] }) {
  // ponytail: drempels per thema (max 24) — N1 t/m 8, N2 9–16, N3 17–24.
  // Alle thema's delen dezelfde maxScore, dus drempels als % zijn constant.
  const maxScore = scores[0]?.maxScore ?? 24;
  const boundLow = (8 / maxScore) * 100;
  const boundHigh = (16 / maxScore) * 100;
  const zoneMid = {
    1: boundLow / 2,
    2: (boundLow + boundHigh) / 2,
    3: (boundHigh + 100) / 2,
  };

  return (
    <div
      className="bloom-overview__chart"
      role="img"
      aria-label={`Balkgrafiek met scores per thema. Niveaugrenzen: ${TIER_LABELS[1]}, ${TIER_LABELS[2]} en ${TIER_LABELS[3]}.`}
    >
      <div className="bloom-overview__chart-bands" aria-hidden="true">
        <span className="bloom-overview__grid-line" style={{ bottom: "0%" }} />
        <span
          className="bloom-overview__grid-line"
          style={{ bottom: `${boundLow}%` }}
        />
        <span
          className="bloom-overview__grid-line"
          style={{ bottom: `${boundHigh}%` }}
        />
        <span
          className="bloom-overview__zone-label"
          style={{ bottom: `${zoneMid[1]}%` }}
        >
          {TIER_LABELS[1]}
        </span>
        <span
          className="bloom-overview__zone-label"
          style={{ bottom: `${zoneMid[2]}%` }}
        >
          {TIER_LABELS[2]}
        </span>
        <span
          className="bloom-overview__zone-label"
          style={{ bottom: `${zoneMid[3]}%` }}
        >
          {TIER_LABELS[3]}
        </span>
      </div>
      <div className="bloom-overview__bars">
        {scores.map((s) => {
          const tier = scoreToTier(s.score, s.maxScore);
          const pct = (s.score / s.maxScore) * 100;
          return (
            <div key={s.themeId} className="bloom-overview__bar-col">
              <div className="bloom-overview__bar-track">
                <div
                  className={`bloom-overview__bar bloom-overview__bar--n${tier}`}
                  style={{ height: `${pct}%` }}
                >
                  <span className="bloom-overview__bar-value">{s.score}</span>
                </div>
              </div>
              <span className="bloom-overview__bar-label">{s.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
